import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import PdfViewer from '@/components/PdfViewer.jsx';
import Seo from '@/components/Seo.jsx';

const HELLOASSO_FORM_URL = 'https://www.helloasso.com/associations/neuvieme-continent/adhesions/adhesion-neuvieme-continent';

function JoinUsPage() {
  return <>
      <Seo
        title="Rejoindre le Neuvième Continent - Adhésion"
        description="Adhérer au Neuvième Continent, c'est intégrer une communauté de passionné·es de jeu de rôle sur table à Nantes."
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-grow relative">
          {/* Hero Section with Background Gradient */}
          <section className="relative py-20 md:py-32 px-4 sm:px-6 lg:px-8" style={{
          background: 'linear-gradient(to bottom, #EF509D, #f27277)'
        }}>
            <div className="max-w-3xl mx-auto relative z-10">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center space-y-6">
                <h1 className="font-pixel text-4xl md:text-5xl lg:text-6xl text-white tracking-wide text-balance drop-shadow-lg">
                  Rejoindre le Neuvième Continent
                </h1>
                <p className="text-lg md:text-xl text-white/90 leading-relaxed text-balance mx-auto drop-shadow-md">
                  Adhérer au Neuvième Continent, c'est intégrer une communauté de passionné·es de jeu de rôle sur table et contribuer à la vie d'une association qui grandit avec ses membres.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Main Content */}
          <div className="relative z-10 py-20 md:py-28 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto space-y-16">

              {/* Avantages + tarif côte à côte sur desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-card border border-border rounded-3xl p-8 shadow-sm">
                  <h2 className="font-elegant text-2xl font-bold text-foreground mb-6 text-center">
                    Avantages de l'adhésion
                  </h2>
                  <ul className="space-y-4">
                    {["l'accès à toutes les séances de jeu organisées par l'association", "une voix délibérative lors des assemblées générales", "la possibilité de vous présenter au conseil d'administration"].map((benefit, index) => <li key={index} className="flex items-start gap-4">
                        <div className="mt-1 bg-primary/10 p-1 rounded-full shrink-0">
                          <Check className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-foreground leading-relaxed">{benefit}</span>
                      </li>)}
                  </ul>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} className="bg-card border border-border rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center text-center">
                  <h2 className="font-elegant text-2xl font-bold text-foreground mb-4">
                    Cotisation annuelle
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    Saison du 1er septembre au 31 août
                  </p>
                  <p className="text-4xl font-bold text-primary mb-2">10 €</p>
                  <p className="text-muted-foreground">
                    ou <span className="font-bold text-primary">5 €</span> pour une adhésion à partir du 1er janvier
                  </p>
                </motion.div>
              </div>

              {/* Bouton d'adhésion : grand CTA vers la page HelloAsso */}
              <section aria-label="Adhérer en ligne via HelloAsso" className="text-center">
                <h2 className="font-elegant text-2xl md:text-3xl font-bold text-foreground mb-8">
                  Adhérer en ligne
                </h2>
                <a
                  href={HELLOASSO_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-4 bg-primary hover:bg-[#EF509D] text-white px-12 py-6 rounded-full font-bold text-xl md:text-2xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 group"
                >
                  <span>Adhérer</span>
                  <img
                    src="https://horizons-cdn.hostinger.com/257434f3-17c6-40ce-944a-86edaf1db41d/a7272dbae9b21682840d60dca0887e5f.png"
                    alt="HelloAsso"
                    className="h-8 md:h-9 w-auto object-contain bg-white rounded-full px-2 py-1 group-hover:scale-105 transition-transform"
                  />
                </a>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto mt-8">
                  Le paiement en ligne est géré par HelloAsso, une plateforme sécurisée dédiée aux associations. HelloAsso se rémunère via une contribution volontaire que vous pouvez ajuster ou supprimer au moment du paiement.
                </p>
              </section>

              {/* Legal Section */}
              <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="pt-8 border-t border-border/50">
                <div className="text-center space-y-6">
                  <p className="text-foreground font-medium">
                    L'adhésion vaut acceptation des Statuts et du Règlement intérieur de l'association.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <PdfViewer
                      url="/documents/statuts-9continent-2026.pdf"
                      title="Statuts — Neuvième Continent (2026)"
                      buttonLabel="Consulter les Statuts"
                    />
                    <PdfViewer
                      url="/documents/reglement-interieur-9continent-2026.pdf"
                      title="Règlement intérieur — Neuvième Continent (2026)"
                      buttonLabel="Consulter le Règlement intérieur"
                    />
                  </div>
                </div>
              </motion.section>

            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>;
}
export default JoinUsPage;
